import type {
  AgendaItem,
  Encounter,
  ListEncountersParams,
  ListEncountersResponse,
  ListMedicalCodesByEncounterResponse,
  PaginatedEncounterListItem,
  Patient,
  PatientSearchItem,
  PatientSearchResult,
  SearchPatientsParams,
} from "@elation/api-client/src/stitch-web";

export const VISIT_TOUR_PATIENT_ID = -1;
export const VISIT_TOUR_ENCOUNTER_ID = -1;
export const VISIT_TOUR_PHYSICIAN_ID = -1;
export const VISIT_TOUR_PRACTICE_ID = -1;

export const VISIT_TOUR_RECENT_VISITS_PARAMS = {
  limit: 10,
  sortBy: "startedAt",
  order: "desc",
} satisfies ListEncountersParams;

export const VISIT_TOUR_PATIENT_SEARCH_PARAMS = {
  limit: 10,
  sortBy: "lastName",
  order: "asc",
} satisfies SearchPatientsParams;

export const VISIT_TOUR_PATIENT_SELECTOR_PARAMS = {
  limit: 6,
  sortBy: "lastName",
  order: "asc",
} satisfies SearchPatientsParams;

const now = "2026-07-02T05:45:00.000Z";
const startedAt = "2026-07-02T05:46:00.000Z";
const completedAt = "2026-07-02T06:05:00.000Z";

export const visitTourPatient = {
  id: VISIT_TOUR_PATIENT_ID,
  firstName: "Margaret",
  middleName: null,
  lastName: "Chen",
  actualName: null,
  dob: "1962-04-11",
  sex: "Female",
  genderIdentity: "not_asked",
  legalGenderMarker: "F",
  pronouns: "none",
  sexualOrientation: "not_asked",
  race: "Asian",
  ethnicity: "No ethnicity specified",
  preferredLanguage: "English",
  preferredContact: "phone",
  addressLine1: "123 Maple Ave",
  addressLine2: null,
  city: "San Francisco",
  state: "CA",
  zip: "94105",
  smsOptInStatus: null,
  primaryCarePhysicianId: VISIT_TOUR_PHYSICIAN_ID,
  primaryCarePracticeId: VISIT_TOUR_PRACTICE_ID,
  status: "active",
  createdAt: now,
  updatedAt: now,
} satisfies Patient;

export const visitTourPatientSearchItem = {
  patientId: VISIT_TOUR_PATIENT_ID,
  externalPatientId: null,
  ehr: null,
  syncStatus: "synced",
  firstName: visitTourPatient.firstName,
  middleName: visitTourPatient.middleName,
  lastName: visitTourPatient.lastName,
  actualName: visitTourPatient.actualName,
  dob: visitTourPatient.dob,
  sex: visitTourPatient.sex,
  addressLine1: visitTourPatient.addressLine1,
  addressLine2: visitTourPatient.addressLine2,
  city: visitTourPatient.city,
  state: visitTourPatient.state,
  zip: visitTourPatient.zip,
} satisfies PatientSearchItem;

export const visitTourEncounter = {
  id: VISIT_TOUR_ENCOUNTER_ID,
  patientId: VISIT_TOUR_PATIENT_ID,
  physicianId: VISIT_TOUR_PHYSICIAN_ID,
  status: "created",
  startedAt: now,
  completedAt: null,
  finalizingAt: null,
  scratchpadNotes: "Patient reports home sugars have been stable.",
  note: null,
} satisfies Encounter;

export const visitTourCompletedEncounter = {
  ...visitTourEncounter,
  status: "completed",
  completedAt,
  note: "62 y/o F with T2DM and hypertension presents for chronic care follow-up.\n\nA1c ordered today; last value 8.1%. BP elevated at 148/90, lisinopril increased to 20 mg daily. Retinal screening ordered for diabetic eye exam. Statin tolerated without recurrent myalgia; continue current dose and recheck CK in 6 weeks.",
} satisfies Encounter;

export const visitTourRecordingEncounter = {
  ...visitTourEncounter,
  status: "in_progress",
  startedAt,
  completedAt: null,
  finalizingAt: null,
  note: null,
} satisfies Encounter;

export const visitTourEncounterListItem = {
  id: VISIT_TOUR_ENCOUNTER_ID,
  patientId: VISIT_TOUR_PATIENT_ID,
  physicianId: VISIT_TOUR_PHYSICIAN_ID,
  status: "completed",
  createdAt: now,
  updatedAt: completedAt,
  startedAt: now,
  completedAt,
  finalizingAt: null,
  closedAt: null,
  agendaItemCount: 4,
  codeCount: 4,
  notePreview:
    "A1c ordered today; BP elevated, lisinopril increased; retinal screening ordered.",
  patient: {
    id: VISIT_TOUR_PATIENT_ID,
    firstName: visitTourPatient.firstName,
    middleName: visitTourPatient.middleName,
    lastName: visitTourPatient.lastName,
  },
} satisfies PaginatedEncounterListItem;

export const visitTourAgendaItems = [
  {
    id: -1,
    description: "A1c screening (HbA1c < 9%)",
    type: "opportunity",
    status: "open",
    patientId: VISIT_TOUR_PATIENT_ID,
    practiceId: VISIT_TOUR_PRACTICE_ID,
    encounterId: VISIT_TOUR_ENCOUNTER_ID,
    careProtocolOpportunityId: null,
    createdByPhysicianId: VISIT_TOUR_PHYSICIAN_ID,
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
    createdAt: now,
    updatedAt: now,
    signalState: "open",
  },
  {
    id: -2,
    description: "Diabetic retinal eye exam",
    type: "opportunity",
    status: "open",
    patientId: VISIT_TOUR_PATIENT_ID,
    practiceId: VISIT_TOUR_PRACTICE_ID,
    encounterId: VISIT_TOUR_ENCOUNTER_ID,
    careProtocolOpportunityId: null,
    createdByPhysicianId: VISIT_TOUR_PHYSICIAN_ID,
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
    createdAt: now,
    updatedAt: now,
    signalState: "open",
  },
  {
    id: -3,
    description: "Controlling high blood pressure",
    type: "opportunity",
    status: "open",
    patientId: VISIT_TOUR_PATIENT_ID,
    practiceId: VISIT_TOUR_PRACTICE_ID,
    encounterId: VISIT_TOUR_ENCOUNTER_ID,
    careProtocolOpportunityId: null,
    createdByPhysicianId: VISIT_TOUR_PHYSICIAN_ID,
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
    createdAt: now,
    updatedAt: now,
    signalState: "open",
  },
  {
    id: -4,
    description: "Review statin tolerance",
    type: "custom",
    status: "open",
    patientId: VISIT_TOUR_PATIENT_ID,
    practiceId: VISIT_TOUR_PRACTICE_ID,
    encounterId: VISIT_TOUR_ENCOUNTER_ID,
    careProtocolOpportunityId: null,
    createdByPhysicianId: VISIT_TOUR_PHYSICIAN_ID,
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
    createdAt: now,
    updatedAt: now,
    signalState: "open",
  },
] satisfies AgendaItem[];

export const visitTourMedicalCodes = {
  codingStatus: "ready",
  icd10: [
    {
      code: "E11.9",
      description: "Type 2 diabetes mellitus without complications",
      creationType: "ai",
      modifier1: null,
      modifier2: null,
      modifier3: null,
      modifier4: null,
      units: null,
      evidence: [
        {
          narrative: "Diabetes follow-up and A1c discussed during the visit.",
          quotes: ["Let's get your A1c drawn today, you're due."],
        },
      ],
    },
    {
      code: "I10",
      description: "Essential hypertension",
      creationType: "ai",
      modifier1: null,
      modifier2: null,
      modifier3: null,
      modifier4: null,
      units: null,
      evidence: [
        {
          narrative: "Blood pressure was elevated and medication adjusted.",
          quotes: ["Your pressure's reading a bit high, 148 over 90."],
        },
      ],
    },
  ],
  cpt: [],
  vbc: [
    {
      code: "3051F",
      description: "A1c ordered",
      creationType: "ai",
      modifier1: null,
      modifier2: null,
      modifier3: null,
      modifier4: null,
      units: null,
      evidence: [
        {
          narrative: "A1c screening was ordered during the encounter.",
          quotes: ["Let's get your A1c drawn today, you're due."],
        },
      ],
    },
    {
      code: "2022F",
      description: "Dilated retinal evaluation ordered",
      creationType: "ai",
      modifier1: null,
      modifier2: null,
      modifier3: null,
      modifier4: null,
      units: null,
      evidence: [
        {
          narrative: "Retinal screening was ordered before the patient left.",
          quotes: ["Let's get you set up for the retinal screening."],
        },
      ],
    },
  ],
} satisfies ListMedicalCodesByEncounterResponse;

export const visitTourPatientSearchResult = {
  errors: {},
  items: [visitTourPatientSearchItem],
  nextCursor: null,
} satisfies PatientSearchResult;

export const visitTourEncountersResponse = {
  items: [visitTourEncounterListItem],
  nextCursor: null,
} satisfies ListEncountersResponse;
